import { RoleEnum } from "@/enum/role"
import { useOperatorMeQuery, useOperatorMePublicQuery } from "@/services/user"
import { useAuthData } from "@/context/auth-data-provider"
import { Badge } from "@/components/ui/badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { User, Shield, RefreshCw } from "lucide-react"
import { GROUP_NAMES, ROUTE_TITLES } from "@/constants/group-config"
import { excludedRoutes } from "@/routes/public-route-map"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { lazy, Suspense } from "react"
import { featureFlags } from "@/config/feature-flag"
import { useLocation } from "react-router-dom"

// Lazy load NotificationDropdown para evitar problemas com RTK Query em rotas públicas
const NotificationDropdown = lazy(() =>
  import("@/components/notifications/NotificationDropdown").then(mod => ({ default: mod.NotificationDropdown }))
);

// Verifica se está em rota pública - calculado fora do componente
const checkIsPublicRoute = (): boolean => {
  if (typeof window === 'undefined') return true;
  return excludedRoutes.some(route => window.location.pathname.startsWith(route));
};

const NavbarHeader = () => {
  const location = useLocation();
  const operatorValidate = localStorage.getItem("@verzel:operator-access");
  const isPublicRoute = checkIsPublicRoute();
  const useKeycloak = featureFlags.USE_KEYCLOAK;

  // Para rotas normais: dados vem do AuthDataProvider (nullable fora do provider)
  const authData = useAuthData();

  // Para rotas de operador: manter endpoints dedicados
  const { data: operatorDataPrivate } = useOperatorMeQuery(undefined, {
    skip: !operatorValidate || useKeycloak,
  });

  const { data: operatorDataPublic } = useOperatorMePublicQuery(undefined, {
    skip: !operatorValidate || !useKeycloak,
  });

  const operatorData = useKeycloak ? operatorDataPublic : operatorDataPrivate;

  const user = operatorValidate ? operatorData : authData?.user;

  const rolesUser = Array.from(
    new Set(
      operatorValidate
        ? operatorData?.user_permission_group?.map((role) => role.role).flat(Number.POSITIVE_INFINITY)
        : user?.user_permission_group?.map((role) => role.role).flat(Number.POSITIVE_INFINITY),
    ),
  )

  const userGroup = authData?.currentModule ?? localStorage.getItem("@verzel:module")

  const routeTitle = Object.entries(ROUTE_TITLES)
    .find(([prefix]) => location.pathname.startsWith(prefix))?.[1]

  const groupDisplayName = routeTitle ?? (userGroup ? GROUP_NAMES[userGroup] : "Retaguarda")

  const isAdmin = rolesUser.includes(RoleEnum.ADMINISTRADOR)
  const userRole = isAdmin ? "Administrador" : "Usuário"

  return (
    <header className="flex h-16 items-center border-b bg-background/95 px-5 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          {!operatorValidate ? (
            <SidebarTrigger className="h-10 w-10 rounded-full border border-border/40 bg-background text-foreground transition-all duration-200 hover:border-[#2E0D1E]/20 hover:bg-[#2E0D1E] hover:text-white hover:shadow-md active:scale-95" />
          ) : (
            <div className="w-10" />
          )}
        </div>

        <div className="flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  window.location.href = window.location.pathname + "?t=" + Date.now();
                }}
                className="relative h-10 w-10 rounded-full transition-all duration-200 hover:bg-[#4B303C]/5 hover:border-[#4B303C]/20 hover:scale-105 active:scale-95"
              >
                <RefreshCw className="h-5 w-5 text-[#4B303C]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px] text-center">
              <p className="font-semibold text-sm">Atualizar portal</p>
              <p className="text-xs text-muted-foreground">
                Tela com erro ou não carregou? Clique aqui para atualizar o portal com a versão mais recente.
              </p>
            </TooltipContent>
          </Tooltip>
          {!isPublicRoute && (
            <Suspense fallback={null}>
              <NotificationDropdown />
            </Suspense>
          )}
          <Badge
            variant="secondary"
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-[#2E0D1E] hover:bg-[#2E0D1E] text-white"
          >

            {isAdmin ? <Shield className="h-4 w-4 relative z-10" /> : <User className="h-4 w-4 relative z-10" />}

            <div className="relative z-10 flex items-center gap-2">
              <span className="font-semibold text-xs">{userRole}</span>
              <span className="opacity-60">•</span>
              <span className="opacity-90 text-xs">{groupDisplayName}</span>
            </div>
          </Badge>

        </div>
      </div>
    </header>
  )
}

export default NavbarHeader
